import {
  Box,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  Column,
  Row,
  Switch,
  Text,
} from "@hightouch/htui";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import type { FeatureMatrixRelationshipPathsQuery } from "src/graphql";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { TextWithTooltip } from "src/components/text-with-tooltip";
import type {
  ColumnsResult,
  FeatureMatrixFormValues,
} from "src/pages/decision-engines/configuration/feature-matrices/types";
import {
  SHOULD_DIRTY_OPTION,
  OVERALL_COUNT_KEY,
  buildSidebarModels,
  buildUnusableModels,
  disambiguateSidebarModels,
  getModelTypeIcon,
} from "src/pages/decision-engines/configuration/feature-matrices/utils";

export function ModelSidebar({
  columnsResult,
  selectedModelKey,
  onSelectModel,
  pathsData,
  parentModelId,
}: {
  columnsResult: ColumnsResult;
  selectedModelKey: string;
  onSelectModel: (key: string) => void;
  pathsData: FeatureMatrixRelationshipPathsQuery | undefined;
  parentModelId: string;
}) {
  const [hideUnused, setHideUnused] = useState(false);
  const [showUnusable, setShowUnusable] = useState(false);
  const { watch, setValue } = useFormContext<FeatureMatrixFormValues>();
  const enabledRelationships = watch("enabledRelationships");
  const relationships = watch("relationships");

  const models = useMemo(() => {
    const built = buildSidebarModels(columnsResult, {
      enabledRelationships,
      relationships,
    });
    return disambiguateSidebarModels(built, pathsData, parentModelId);
  }, [
    columnsResult,
    enabledRelationships,
    relationships,
    pathsData,
    parentModelId,
  ]);

  const unusableModels = useMemo(
    () => buildUnusableModels(columnsResult),
    [columnsResult],
  );

  const scrollFade = useScrollFade();

  return (
    <Column
      width="320px"
      minWidth="320px"
      borderRight="1px solid"
      borderColor="base.border"
      bg="base.lightBackground"
      overflow="hidden"
    >
      <Row px={4} py={3} justify="space-between" align="center" flexShrink={0}>
        <Text fontWeight="medium">Models</Text>
        <Button
          variant="tertiary"
          size="sm"
          color={hideUnused ? "primary.base" : "text.secondary"}
          onClick={() => setHideUnused((v) => !v)}
        >
          {hideUnused ? "Show all" : "Hide unused"}
        </Button>
      </Row>
      <Column
        ref={scrollFade.ref}
        flex={1}
        overflow="auto"
        sx={scrollFade.style}
      >
        {models.map((model) => {
          const Icon = getModelTypeIcon(model.type);
          const isSelected = model.key === selectedModelKey;
          const isEnabled = enabledRelationships[model.key] ?? false;
          const relKeys = Object.keys(relationships[model.key] ?? {});
          const hasOverallCount = relKeys.includes(OVERALL_COUNT_KEY);
          const selectedCount = relKeys.filter(
            (k) => k !== OVERALL_COUNT_KEY,
          ).length;
          const totalCount = selectedCount + (hasOverallCount ? 1 : 0);

          if (
            hideUnused &&
            model.type !== "parent" &&
            !isEnabled &&
            totalCount === 0
          ) {
            return null;
          }

          return (
            <Row
              key={model.key}
              px={4}
              py={3}
              gap={3}
              align="center"
              cursor="pointer"
              bg={isSelected ? "primary.background" : undefined}
              _hover={isSelected ? undefined : { bg: "base.background" }}
              onClick={() => onSelectModel(model.key)}
              borderLeft="3px solid"
              borderLeftColor={isSelected ? "primary.base" : "transparent"}
            >
              {model.type === "parent" ? (
                <Box visibility="hidden" aria-hidden>
                  <Switch
                    isChecked={false}
                    aria-label="hidden"
                    size="sm"
                    onChange={() => {}}
                  />
                </Box>
              ) : (
                <Switch
                  isChecked={isEnabled}
                  onChange={(checked) => {
                    setValue(
                      `enabledRelationships.${model.key}`,
                      checked,
                      SHOULD_DIRTY_OPTION,
                    );
                  }}
                  size="sm"
                  aria-label={`Toggle ${model.name}`}
                />
              )}
              <Icon size="lg" />
              <Column flex={1} minWidth={0}>
                <Text color="text.secondary" size="sm">
                  {model.label}
                </Text>
                <TextWithTooltip fontWeight="medium">
                  {model.name}
                </TextWithTooltip>
              </Column>
              <Text color="text.secondary" size="sm">
                {totalCount > 0 ? totalCount : "--"}
              </Text>
            </Row>
          );
        })}
        {unusableModels.length > 0 && (
          <Column mt={2} borderTop="1px solid" borderColor="base.border">
            <Row
              px={4}
              py={3}
              gap={2}
              align="center"
              cursor="pointer"
              _hover={{ bg: "base.background" }}
              onClick={() => setShowUnusable((v) => !v)}
            >
              {showUnusable ? (
                <ChevronDownIcon color="text.secondary" />
              ) : (
                <ChevronRightIcon color="text.secondary" />
              )}
              <Text size="sm" fontWeight="medium" color="text.secondary">
                Unavailable models ({unusableModels.length})
              </Text>
            </Row>
            {showUnusable &&
              unusableModels.map((model) => {
                const Icon = getModelTypeIcon(model.type);
                return (
                  <Row
                    key={model.relationshipId}
                    px={4}
                    py={3}
                    gap={3}
                    align="center"
                    opacity={0.7}
                  >
                    <Icon size="lg" />
                    <Column flex={1} minWidth={0}>
                      <TextWithTooltip fontWeight="medium">
                        {model.name}
                      </TextWithTooltip>
                      <Text color="text.secondary" size="sm">
                        {model.mergedIntoNames.length > 0
                          ? `Columns available under ${model.mergedIntoNames.join(", ")}`
                          : "Columns are merged into another model"}
                      </Text>
                    </Column>
                  </Row>
                );
              })}
          </Column>
        )}
      </Column>
    </Column>
  );
}
